import { useState, useEffect } from 'react';
import { Save01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { useMe } from '@/hooks/api/use-auth';
import { useUpdateProfile } from '@/hooks/api/use-profile';
import { useTranslation } from 'react-i18next';

export const ProfileAccountForm = () => {
    const { t } = useTranslation(['pages:profile', 'common']);
    const { data: currentUser, isLoading } = useMe();
    const updateProfile = useUpdateProfile();
    
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        job_title: '',
        department: '',
    });

    useEffect(() => {
        if (currentUser) {
            // Use first_name and last_name if available, otherwise parse from full_name
            const firstName = currentUser.first_name || currentUser.full_name?.split(' ')[0] || '';
            const lastName = currentUser.last_name || currentUser.full_name?.split(' ').slice(1).join(' ') || '';
            
            setFormData({
                first_name: firstName,
                last_name: lastName,
                email: currentUser.email || '',
                phone: currentUser.phone || '',
                job_title: currentUser.job_title || '',
                department: currentUser.department || '',
            });
        }
    }, [currentUser]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        await updateProfile.mutateAsync({
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone || undefined,
            job_title: formData.job_title || undefined,
            department: formData.department || undefined,
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="text-tertiary">{t('common:loading')}</div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                    label={t('account.firstName')}
                    placeholder={t('account.firstNamePlaceholder')}
                    value={formData.first_name}
                    onChange={(value) => setFormData({ ...formData, first_name: value })}
                    size="md"
                />
                <Input
                    label={t('account.lastName')}
                    placeholder={t('account.lastNamePlaceholder')}
                    value={formData.last_name}
                    onChange={(value) => setFormData({ ...formData, last_name: value })}
                    size="md"
                />
            </div>

            <Input
                label={t('account.email')}
                placeholder={t('account.emailPlaceholder')}
                value={formData.email}
                onChange={(value) => setFormData({ ...formData, email: value })}
                type="email"
                size="md"
                isRequired
            />

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                    label={t('account.phone')}
                    placeholder={t('account.phonePlaceholder')}
                    value={formData.phone}
                    onChange={(value) => setFormData({ ...formData, phone: value })}
                    size="md"
                />
                <Input
                    label={t('account.jobTitle')}
                    placeholder={t('account.jobTitlePlaceholder')}
                    value={formData.job_title}
                    onChange={(value) => setFormData({ ...formData, job_title: value })}
                    size="md"
                />
            </div>

            <Input
                label={t('account.department')}
                placeholder={t('account.departmentPlaceholder')}
                value={formData.department}
                onChange={(value) => setFormData({ ...formData, department: value })}
                size="md"
            />

            <div className="flex items-center justify-end gap-4 pt-4 border-t border-secondary">
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                    iconLeading={Save01}
                    isDisabled={updateProfile.isPending}
                >
                    {updateProfile.isPending ? t('common:saving') : t('common:save')}
                </Button>
            </div>
        </form>
    );
};

